import { Suspense, useRef, useState, useEffect, useCallback } from 'react'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { Canvas, useFrame, useThree, extend } from '@react-three/fiber'
import {
  Stack, CircularProgress, Box, IconButton, Tooltip, Divider,
  ToggleButtonGroup, ToggleButton, Popover, Typography, Slider
} from '@mui/material'
import { useTheme, alpha } from '@mui/material/styles'
import {
  Home, ViewInAr, ArrowUpward, ArrowDownward, ArrowBack, ArrowForward,
  FlipToFront, FlipToBack, ThreeSixty, ZoomIn, ZoomOut, MoreVert, Layers,
  GridOn, GridOff, Brightness4, Brightness5
} from '@mui/icons-material'
import RectangleIcon from '@mui/icons-material/Rectangle'
import RectangleOutlinedIcon from '@mui/icons-material/RectangleOutlined'
import RectangleTwoToneIcon from '@mui/icons-material/RectangleTwoTone'
import Proptypes from 'prop-types'
import { Vector3 } from 'three'
import { ModelLoader } from './ModelLoader'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'

extend({ OrbitControls })

const SmartThreejs = ({ modelType = 'PLY', isPreviewLoading, setIsPreviewLoading, modelUrl }) => {
  const { t_ } = useSmartTranslation()

  const [modelBounds, setModelBounds] = useState({ radius: 0, minY: 0, dimensions: null })
  const [loadProgress, setLoadProgress] = useState(0)

  const [isAutoRotate, setIsAutoRotate] = useState(true)
  const [displayMode, setDisplayMode] = useState('solid')
  const [showGrid, setShowGrid] = useState(true)
  const [showShadows, setShowShadows] = useState(true)

  const [sliderValue, setSliderValue] = useState(1)
  const [edgesAngle, setEdgesAngle] = useState(1)

  const [displayAnchorEl, setDisplayAnchorEl] = useState(null)
  const displayPopoverOpen = Boolean(displayAnchorEl)

  const [viewsAnchorEl, setViewsAnchorEl] = useState(null)
  const viewsPopoverOpen = Boolean(viewsAnchorEl)

  const orbitControlsRef = useRef()
  const targetCameraPosRef = useRef(null)
  const [modelError, setModelError] = useState(false)

  useEffect(() => {
    setModelError(false)
    setLoadProgress(0)
  }, [modelUrl, modelType])

  const theme = useTheme()

  const handleDisplayModeChange = (event, newMode) => {
    if (newMode !== null) setDisplayMode(newMode)
  }

  const handleViewChange = useCallback((position) => {
    if (!orbitControlsRef.current) return
    setIsAutoRotate(false)

    const targetCenter = new Vector3(0, 0, 0)
    const direction = new Vector3(...position).normalize()
    const safeDistance = modelBounds.radius > 0 ? modelBounds.radius * 5 : 5

    targetCameraPosRef.current = targetCenter.clone().add(direction.multiplyScalar(safeDistance))
  }, [modelBounds.radius])

  const handleResetCamera = useCallback((stopAutoRotate = true) => {
    if (!orbitControlsRef.current) return
    if (stopAutoRotate === true) setIsAutoRotate(false)

    targetCameraPosRef.current = null

    const camera = orbitControlsRef.current.object
    const controls = orbitControlsRef.current

    const safeSize = modelBounds.radius > 0 ? modelBounds.radius : 1
    const targetCenter = new Vector3(0, 0, 0)
    const safeDistance = safeSize * 5
    const direction = new Vector3(1, 1, 1).normalize()

    camera.position.copy(targetCenter.clone().add(direction.multiplyScalar(safeDistance)))
    camera.near = safeSize * 0.01
    camera.far = safeSize * 100

    const canvasHeight = controls.domElement.clientHeight || window.innerHeight
    camera.zoom = canvasHeight / (safeSize * 2.5)

    camera.lookAt(targetCenter)
    camera.updateProjectionMatrix()

    controls.target.copy(targetCenter)
    controls.update()
  }, [modelBounds.radius])

  const handleZoom = useCallback((direction) => {
    if (!orbitControlsRef.current) return

    const camera = orbitControlsRef.current.object
    const controls = orbitControlsRef.current

    const safeSize = modelBounds.radius > 0 ? modelBounds.radius : 1
    const canvasHeight = controls.domElement.clientHeight || window.innerHeight
    const baseZoom = canvasHeight / (safeSize * 2.5)

    const maxZoom = baseZoom * 10
    const minZoom = baseZoom * 0.1
    const zoomFactor = 1.2

    if (direction === 'in') {
      camera.zoom = Math.min(camera.zoom * zoomFactor, maxZoom)
    } else {
      camera.zoom = Math.max(camera.zoom / zoomFactor, minZoom)
    }

    camera.updateProjectionMatrix()
    controls.update()
  }, [modelBounds.radius])

  // Global Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

      switch (e.key.toLowerCase()) {
        case '+':
        case '=': handleZoom('in'); break
        case '-':
        case '_': handleZoom('out'); break
        case 'r': handleResetCamera(true); break
        case 'g': setShowGrid(prev => !prev); break
        case 's': setShowShadows(prev => !prev); break // Added 'S' for shadows
        default: break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleZoom, handleResetCamera])

  const ViewIconButton = ({ title, icon: Icon, position }) => (
    <Tooltip title={title} placement='top' arrow>
      <IconButton onClick={() => handleViewChange(position)} sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
        <Icon />
      </IconButton>
    </Tooltip>
  )

  const onSetModelSize = useCallback((newBounds) => {
    setModelBounds(prev => {
      if (prev.radius === newBounds.radius && prev.minY === newBounds.minY) return prev
      return newBounds
    })
  }, [])

  useEffect(() => {
    if (modelBounds.radius > 0 && orbitControlsRef.current) {
      handleResetCamera(false)
    }
  }, [modelBounds.radius, handleResetCamera])

  const CameraControls = ({ controls, radius, autoRotate, setAutoRotate }) => {
    const { camera, gl: { domElement } } = useThree()

    useEffect(() => {
      const orbit = controls.current
      if (!orbit) return

      const handleInteraction = () => setAutoRotate(false)
      orbit.addEventListener('start', handleInteraction)
      return () => orbit.removeEventListener('start', handleInteraction)
    }, [controls, setAutoRotate])

    useFrame((state, delta) => {
      if (targetCameraPosRef.current && controls.current) {
        const targetCenter = new Vector3(0, 0, 0)
        camera.position.lerp(targetCameraPosRef.current, 10 * delta)
        const threshold = Math.max(radius * 0.05, 0.001)

        if (camera.position.distanceTo(targetCameraPosRef.current) < threshold) {
          camera.position.copy(targetCameraPosRef.current)
          targetCameraPosRef.current = null
        }
        camera.lookAt(targetCenter)
        camera.updateProjectionMatrix()
      }
      controls.current?.update()
    })

    return (
      <orbitControls ref={controls} args={[camera, domElement]} enableDamping dampingFactor={0.05} autoRotate={autoRotate} autoRotateSpeed={2.5} />
    )
  }

  const glassmorphismStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 1,
    padding: '6px 16px',
    borderRadius: '50px',
    backgroundColor: alpha(theme.palette.background.paper, 0.75),
    backdropFilter: 'blur(12px)',
    boxShadow: theme.shadows[4],
    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
  }

  if (modelError) return <Typography color='error'>{t_('Error loading 3D model')}</Typography>

  const safeSize = modelBounds.radius > 0 ? modelBounds.radius : 1

  return (
    <Box sx={{ width: '100%', height: '100%', flexGrow: 1, position: 'relative', minHeight: 0 }}>

      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', background: 'radial-gradient(circle at 50% 50%, #ffffff 0%, #e0e5ec 100%)' }}>

        {/* Loading Progress UI */}
        {isPreviewLoading && (
          <Stack sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, alignItems: 'center', gap: 1 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress variant={loadProgress > 0 ? 'determinate' : 'indeterminate'} value={loadProgress} size={60} />
              {loadProgress > 0 && (
                <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant='caption' component='div' color='text.secondary' sx={{ fontWeight: 'bold' }}>
                    {`${loadProgress}%`}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography variant='body2' color='text.secondary'>{t_('Loading Model...')}</Typography>
          </Stack>
        )}

        <Canvas orthographic shadows style={{ visibility: isPreviewLoading ? 'hidden' : 'visible' }}>
          <Suspense fallback={null}>

            <ModelLoader
              url={modelUrl}
              modelType={modelType}
              setModelSize={onSetModelSize}
              onProgress={setLoadProgress}
              solid={displayMode === 'solid' || displayMode === 'both'}
              edges={displayMode === 'wireframe' || displayMode === 'both'}
              edgesColor='#333333'
              edgesMinAngle={edgesAngle}
              setIsPreviewLoading={setIsPreviewLoading}
              setModelError={setModelError}
            />

            <hemisphereLight skyColor='#ffffff' groundColor='#b0bec5' intensity={0.4} />

            <directionalLight
              castShadow={showShadows} // Toggles shadows dynamically
              position={[safeSize * 2, safeSize * 3, safeSize * 2]}
              intensity={0.7}
              color='#fffaf0'
              shadow-mapSize={[1024, 1024]}
              shadow-camera-left={-safeSize * 2}
              shadow-camera-right={safeSize * 2}
              shadow-camera-top={safeSize * 2}
              shadow-camera-bottom={-safeSize * 2}
              shadow-camera-near={0.1}
              shadow-camera-far={safeSize * 10}
            />

            <directionalLight position={[-safeSize * 2, -safeSize, -safeSize * 2]} intensity={0.3} color='#e6f2ff' />
            <directionalLight position={[0, safeSize * 3, -safeSize * 2]} intensity={0.5} color='#ffffff' />

            {/* Ground Plane to Catch Shadows */}
            {showShadows && (
              <mesh position={[0, modelBounds.minY, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
                <planeGeometry args={[safeSize * 15, safeSize * 15]} />
                <shadowMaterial opacity={0.15} />
              </mesh>
            )}

            {showGrid && (
              <gridHelper args={[safeSize * 15, 60, '#a0aaba', '#d2d7df']} position={[0, modelBounds.minY, 0]} />
            )}

            <CameraControls controls={orbitControlsRef} radius={modelBounds.radius} autoRotate={isAutoRotate} setAutoRotate={setIsAutoRotate} />
          </Suspense>
        </Canvas>

        {!isPreviewLoading && (
          <>
            {/* TOOLBAR: Zoom Controls */}
            <Box sx={{ position: 'absolute', left: '24px', bottom: '24px', zIndex: 20, ...glassmorphismStyle }}>
              <Tooltip title={t_('Zoom In (+)')} placement='top' arrow>
                <IconButton onClick={() => handleZoom('in')} sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}><ZoomIn /></IconButton>
              </Tooltip>
              <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 1, borderColor: 'divider' }} />
              <Tooltip title={t_('Zoom Out (-)')} placement='top' arrow>
                <IconButton onClick={() => handleZoom('out')} sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}><ZoomOut /></IconButton>
              </Tooltip>
            </Box>

            {/* TOOLBAR: Settings & Navigation */}
            <Box sx={{ position: 'absolute', right: '24px', bottom: '24px', zIndex: 20, ...glassmorphismStyle }}>

              <Tooltip title={t_('Display Options')} placement='top' arrow>
                <IconButton onClick={(e) => setDisplayAnchorEl(e.currentTarget)} sx={{ color: displayPopoverOpen ? 'primary.main' : 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}><Layers /></IconButton>
              </Tooltip>

              <Tooltip title={t_(showShadows ? 'Hide Shadows (S)' : 'Show Shadows (S)')} placement='top' arrow>
                <IconButton onClick={() => setShowShadows(!showShadows)} sx={{ color: showShadows ? 'primary.main' : 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
                  {showShadows ? <Brightness4 /> : <Brightness5 />}
                </IconButton>
              </Tooltip>

              <Tooltip title={t_(showGrid ? 'Hide Grid (G)' : 'Show Grid (G)')} placement='top' arrow>
                <IconButton onClick={() => setShowGrid(!showGrid)} sx={{ color: showGrid ? 'primary.main' : 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
                  {showGrid ? <GridOn /> : <GridOff />}
                </IconButton>
              </Tooltip>

              <Tooltip title={t_(isAutoRotate ? 'Stop Auto-Rotate' : 'Start Auto-Rotate')} placement='top' arrow>
                <IconButton onClick={() => setIsAutoRotate(!isAutoRotate)} sx={{ color: isAutoRotate ? 'primary.main' : 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}>
                  <ThreeSixty />
                </IconButton>
              </Tooltip>

              <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 1, borderColor: 'divider' }} />

              <Tooltip title={t_('Reset View (R)')} placement='top' arrow>
                <IconButton onClick={() => handleResetCamera(true)} sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}><Home /></IconButton>
              </Tooltip>

              <Tooltip title={t_('More Views')} placement='top' arrow>
                <IconButton onClick={(e) => setViewsAnchorEl(e.currentTarget)} sx={{ color: viewsPopoverOpen ? 'primary.main' : 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}><MoreVert /></IconButton>
              </Tooltip>
            </Box>

            {/* POPOVER: Display Options */}
            <Popover
              open={displayPopoverOpen} anchorEl={displayAnchorEl} onClose={() => setDisplayAnchorEl(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              sx={{ mb: 1.5 }}
              slotProps={{ paper: { sx: { borderRadius: 3, p: 2.5, minWidth: 220, bgcolor: alpha(theme.palette.background.paper, 0.95), backdropFilter: 'blur(12px)', boxShadow: theme.shadows[8] } } }}
            >
              <Typography variant='overline' sx={{ fontWeight: 'bold', color: 'text.primary', display: 'block', mb: 1.5, lineHeight: 1 }}>{t_('Render Mode')}</Typography>
              <ToggleButtonGroup
                value={displayMode} exclusive onChange={handleDisplayModeChange} size='medium' fullWidth
                sx={{ mb: displayMode !== 'solid' ? 2 : 0, height: 40, '& .MuiToggleButton-root': { color: 'text.secondary', '&.Mui-selected': { backgroundColor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' } } }}
              >
                <Tooltip title={t_('Solid Only')} placement='bottom' arrow><ToggleButton value='solid'><RectangleIcon /></ToggleButton></Tooltip>
                <Tooltip title={t_('Wireframe Only')} placement='bottom' arrow><ToggleButton value='wireframe'><RectangleOutlinedIcon /></ToggleButton></Tooltip>
                <Tooltip title={t_('Solid + Wireframe')} placement='bottom' arrow><ToggleButton value='both'><RectangleTwoToneIcon /></ToggleButton></Tooltip>
              </ToggleButtonGroup>

              {displayMode !== 'solid' && (
                <Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant='overline' sx={{ fontWeight: 'bold', color: 'text.primary', display: 'block', mb: 0.5, lineHeight: 1 }}>{t_('Wireframe Detail')}</Typography>
                  <Typography variant='caption' sx={{ display: 'block', mb: 2, color: 'text.secondary', lineHeight: 1.3 }}>{t_('Lower angle shows more lines')}</Typography>
                  <Box sx={{ px: 1 }}>
                    <Slider value={sliderValue} onChange={(e, newValue) => setSliderValue(newValue)} onChangeCommitted={(e, newValue) => setEdgesAngle(newValue)} valueLabelDisplay='auto' valueLabelFormat={(val) => `${val}°`} step={1} min={0} max={90} size='medium' color='primary' />
                  </Box>
                </Box>
              )}
            </Popover>

            {/* POPOVER: Extra Camera Views */}
            <Popover
              open={viewsPopoverOpen} anchorEl={viewsAnchorEl} onClose={() => setViewsAnchorEl(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              sx={{ mb: 1.5 }}
              slotProps={{ paper: { sx: { borderRadius: 3, p: 1, bgcolor: alpha(theme.palette.background.paper, 0.95), backdropFilter: 'blur(12px)', boxShadow: theme.shadows[8], display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 } } }}
            >
              <ViewIconButton title={t_('Back')} icon={FlipToBack} position={[0, 0, -1]} />
              <ViewIconButton title={t_('Top')} icon={ArrowUpward} position={[0, 1, 0]} />
              <ViewIconButton title={t_('Front')} icon={FlipToFront} position={[0, 0, 1]} />
              <ViewIconButton title={t_('Left')} icon={ArrowBack} position={[-1, 0, 0]} />
              <ViewIconButton title={t_('Isometric')} icon={ViewInAr} position={[1, 1, 1]} />
              <ViewIconButton title={t_('Right')} icon={ArrowForward} position={[1, 0, 0]} />
              <div />
              <ViewIconButton title={t_('Bottom')} icon={ArrowDownward} position={[0, -1, 0]} />
            </Popover>
          </>
        )}
      </Box>
    </Box>
  )
}

SmartThreejs.propTypes = {
  modelType: Proptypes.oneOf(['PLY', 'OBJ', 'STL', 'GLB']).isRequired,
  modelUrl: Proptypes.string.isRequired,
  isPreviewLoading: Proptypes.bool.isRequired,
  setIsPreviewLoading: Proptypes.func.isRequired
}

export default SmartThreejs
